import type { ProductPageContent } from "./types";

/**
 * Mimir is a preview product: there is no shipping codebase behind it yet, so
 * this page deliberately carries no evidence section, no editions and no
 * transactional CTA. It states the problem and the intended approach, and the
 * only thing it asks for is a conversation.
 *
 * If an evidence block ever appears here, it must come from a published harness
 * the way the Cerberus figures do.
 */
export const MIMIR: ProductPageContent = {
  slug: "mimir",
  proofClaim:
    "A poisoned memory keeps working long after the injected page is gone — and nothing in the transcript looks wrong.",
  lede:
    "Mimir is decoy memory defense for agents that remember. It seeds records no legitimate task should ever act on, so an agent reaching for one reveals that its memory has been tampered with.",
  availability:
    "Preview. Mimir is in design with early partners and is not generally available — this page describes the approach, not a shipping product.",
  ctas: [
    { label: "Talk to us about the preview", href: "/contact?topic=mimir" },
    { label: "See how Cerberus watches memory", href: "/cerberus#capabilities" },
  ],
  problem: {
    heading: "Persistent memory turns a one-off injection into a standing instruction",
    body:
      "Runtime defense judges the tool call in front of it. But once an attacker's instruction is written into long-term memory, every later session retrieves it as though the organization had authored it — and the retrieval looks exactly like ordinary recall.",
    points: [
      {
        title: "1 · The injection outlives the session",
        body: "The malicious page can be taken down and the ticket closed while the instruction it planted keeps being retrieved.",
      },
      {
        title: "2 · Retrieval is indistinguishable from recall",
        body: "The agent is not being attacked at that moment; it is remembering. Nothing about the transcript signals compromise.",
      },
      {
        title: "3 · You cannot audit what you cannot query",
        body: "Without provenance on each memory, there is no way to ask which records came from untrusted content and which were authored deliberately.",
      },
    ],
  },
  capabilities: {
    heading: "Seed, detect, prove",
    lede:
      "The intended approach, described so you can judge it before it ships rather than after.",
    items: [
      {
        title: "Seeded decoy records",
        body: "Plausible but fictitious memories that no legitimate task has any reason to act on. Retrieval or use of one is a signal on its own.",
      },
      {
        title: "Detection without a classifier",
        body: "A decoy being used is a fact rather than a probability, which avoids the false-positive problem that dogs content-based memory scanning.",
      },
      {
        title: "Provenance for the whole store",
        body: "Track which records originated in untrusted content so a suspect memory can be traced back to the ingestion that created it.",
      },
      {
        title: "Evidence a reviewer can replay",
        body: "The decoy, the retrieval and the resulting action recorded together, so an incident review reads as a query rather than an excavation.",
      },
    ],
  },
  services: {
    heading: "Audit the memory you already have",
    body:
      "Mimir is not shipping yet, but the audit is available now: Warden reviews how your agents store, retrieve and trust memory, and where an injected record would survive.",
    links: [
      { label: "Memory & Provenance Audit", href: "/warden/provenance-audit" },
      { label: "Agent Readiness Assessment", href: "/warden/agent-readiness" },
    ],
  },
  resources: {
    heading: "Go deeper",
    items: [
      { kind: "Product", label: "Cerberus — runtime tool-boundary defense", href: "/cerberus" },
      { kind: "Research", label: "Agentic attack research", href: "/research" },
      { kind: "Solution", label: "Secure Agentic AI", href: "/secure-agentic-ai" },
    ],
  },
  close: {
    heading: "Shape it while it is still being designed",
    body:
      "We would rather build this against real memory architectures than assumptions. If your agents remember across sessions, we want to hear how.",
    ctas: [
      { label: "Talk to us about the preview", href: "/contact?topic=mimir" },
      { label: "Request a briefing", href: "/warden/briefings" },
    ],
  },
};
